// Thin client over the per-user dashboard card preference API (which cards are
// shown, and where they sit).
//   GET    /auth/dashboard-prefs/  → { prefs }
//   PUT    /auth/dashboard-prefs/  → { success, prefs }
//   DELETE /auth/dashboard-prefs/  → reset to default (all cards, default order)
//
// Its own service for the same reason sidebarThemeService is: the three
// preferences sharing UserNavigationPreference are edited by three unrelated
// surfaces, and a combined client would let each clobber the others' fields.
//
// Wire shape is {version, hidden, order} — `order` was added in version 2 and
// is optional, so a stored version-1 value (hidden only) still reads back fine:
// the sanitizer resolves the missing order to the default layout.

import api from './api';
import {
  DashboardCardId,
  DashboardLayout,
  sanitizeDashboardLayout,
  sanitizeHiddenCards,
} from '../config/dashboardCards';

export interface DashboardPrefs {
  hidden: DashboardCardId[];
  layout: DashboardLayout;
}

const PREFS_VERSION = 2;

interface StoredPrefs {
  version?: number;
  hidden?: unknown;
  order?: unknown;
  spans?: unknown;
  height_mode?: unknown;
}

interface PrefsResponse {
  prefs: StoredPrefs | Record<string, never>;
}

interface SavePrefsResponse extends PrefsResponse {
  success: boolean;
}

const toPrefs = (raw: StoredPrefs | Record<string, never> | undefined): DashboardPrefs => {
  const stored = raw as StoredPrefs | undefined;
  const order = stored?.order && typeof stored.order === 'object' ? stored.order : {};
  // `order` (columns) and `spans` (widths) are separate keys on the wire but
  // one object to the sanitizer, which is the only thing that decides what a
  // valid layout looks like.
  return {
    hidden: sanitizeHiddenCards(stored?.hidden),
    layout: sanitizeDashboardLayout({
      ...order,
      spans: stored?.spans,
      heightMode: stored?.height_mode,
    }),
  };
};

export const dashboardPrefsService = {
  /** Returns the preference already reconciled against the card registry. */
  async get(): Promise<DashboardPrefs> {
    const res = await api.get<PrefsResponse>('/auth/dashboard-prefs/');
    return toPrefs(res?.prefs);
  },

  async save(prefs: DashboardPrefs): Promise<DashboardPrefs> {
    const res = await api.put<SavePrefsResponse>('/auth/dashboard-prefs/', {
      prefs: {
        version: PREFS_VERSION,
        hidden: prefs.hidden,
        order: { main: prefs.layout.main, rail: prefs.layout.rail },
        spans: prefs.layout.spans,
        height_mode: prefs.layout.heightMode,
      },
    });
    // Trust the echo when there is one, but never let a thin response wipe what
    // the user just chose.
    return res?.prefs && Object.keys(res.prefs).length > 0 ? toPrefs(res.prefs) : prefs;
  },

  async reset(): Promise<void> {
    await api.delete('/auth/dashboard-prefs/');
  },
};

export default dashboardPrefsService;
